import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { compose, withProps, withPropsOnChange } from 'recompose';
import { get, range, includes } from 'lodash';
import classnames from 'classnames';

import { withSegmentContext, withProductListViewedEcommerceTracking } from '@sparefoot/react-core/segment';
import { withShowHide } from '@sparefoot/react-core/decorators';
import { buildSearchLink } from 'utils/search';
import pluralize from 'utils/pluralize';

import { withPagination } from 'decorators';
import { Group } from 'components/core/Page';
import { Link } from 'components/core/Link';
import { Text, H3 } from 'components/core/Typography';
import { Button } from 'components/core/Button';
import { NearbyFacilityRow } from 'components/facility/FacilityCard';
import { CardMap } from 'components/search/SearchMap';

import { Pin, List, Chevron } from 'components/svg';

import './nearbyFacilitiesTabbedCard.scss';

// Make sure to import withNearbyFacilities decorator into the searchPage

export const enhance = compose(
	withSegmentContext,
	withProps(({ location, vipAccountId, nearbyFacilities }) => ({
		searchLink: buildSearchLink({
			location: location.zip !== null ? `${location.zip}` : `${location.city}, ${location.state}`,
			accountId: vipAccountId
		}, true),
		paginationItems: nearbyFacilities,
		nearestDistance: get(nearbyFacilities, '[0].distance')
	})),
	withPagination,
	withPropsOnChange(['nearbyFacilities'],
		({ resetPagination }) => {
			resetPagination();
		}),
	withShowHide('list'),
	withProductListViewedEcommerceTracking(
		({ nearbyFacilities, facilitiesIds }) => ({
			products: nearbyFacilities.map((facility, index) => ({
				id: facility.id,
				name: facility.name,
				position: index + 1,
				distance: facility.distance,
				inSearchResults: includes(facilitiesIds, facility.id)
			})),
			productIds: nearbyFacilities.map((f) => f.id)
		}),
		'Nearby Product List Viewed'
	)
);

export function NearbyFacilitiesTabbedCard({
	name,
	searchLink,
	listShowing,
	onToggleList,
	totalPageCount,
	displayedItems,
	nearbyFacilities,
	jumpTo,
	handlePaginationClick,
	visiblePage,
	nearestDistance
}) {
	return (
		<Group className="nearby-facilities-tabbed-card">
			<div className="card-header">
				<H3
					color="black"
					weight="extraBold"
					size="small"
					className="primary-title"
				>
					Find the Closest {name} Facilities
				</H3>
				{!!nearestDistance &&
					<Text color="priceBlue" size="tiny" weight="extraBold">
						As close as {nearestDistance}{' '}
						{pluralize('mile', nearestDistance)} away!
					</Text>
				}
			</div>
			{listShowing ?
				<Fragment>
					{displayedItems.map((facility) => (
						<NearbyFacilityRow
							facility={facility}
							key={facility.name}
							segmentProperties={{ facilityId: facility.id }}
						/>
					))}
				</Fragment> :
				<Group>
					<CardMap facilities={nearbyFacilities} />
				</Group>
			}
			<div className="card-footer">
				{listShowing &&
					<div className="pagination">
						<Link
							onClick={() => handlePaginationClick(-1)}
							segmentLabel="see all nearby facilities"
							className="left-arrow"
						>
							<Chevron className="arrows" direction="left" />
						</Link>
						{range(1, totalPageCount + 1).map((i) => (
							<Link
								onClick={() => jumpTo(i)}
								segmentLabel="see all nearby facilities"
								key={i}
								className={classnames('circles', { 'active-page': visiblePage === i })}
							>
								&#9679;
							</Link>
						))}
						<Link
							onClick={() => handlePaginationClick(1)}
							segmentLabel="see all nearby facilities"
							className="right-arrow"
						>
							<Chevron className="arrows" />
						</Link>
					</div>
				}
				<div className="navigations">
					<div className="switcher">
						<Button
							className={classnames('switch', { active: !listShowing })}
							onClick={onToggleList}
							segmentLabel="switch to map"
						>
							<Pin />
						</Button>
						<Button
							className={classnames('switch', { active: listShowing })}
							onClick={onToggleList}
							segmentLabel="switch to list"
						>
							<List />
						</Button>
					</div>
					<div className="see-all">
						<Link segmentLabel="see all nearby facilities" to={searchLink}>
							<Text color="red" size="tiny" weight="standard">
								More deals from our trusted partner
							</Text>
							{' '}
							<Text color="red" size="tiny">
								&raquo;
							</Text>
						</Link>
					</div>
				</div>
			</div>
		</Group>
	);
}

NearbyFacilitiesTabbedCard.propTypes = {
	displayedItems: PropTypes.arrayOf(PropTypes.object),
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	handlePaginationClick: PropTypes.func,
	jumpTo: PropTypes.func,
	name: PropTypes.string,
	searchLink: PropTypes.string.isRequired,
	listShowing: PropTypes.bool,
	onToggleList: PropTypes.func,
	totalPageCount: PropTypes.number,
	visiblePage: PropTypes.number,
	nearestDistance: PropTypes.number
};

export default enhance(NearbyFacilitiesTabbedCard);
